import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { BookingProvider } from "@/components/Booking";
import { Nav } from "@/components/Nav";
import { Hero } from "@/components/Hero";
import { Growing, B2B, D2C, PricingStrip } from "@/components/Sections1";
import { Audit, Industries, Integrations } from "@/components/Sections2";
import { Testimonials, Agents, FinalCTA, Footer } from "@/components/Sections3";
import { Seo, HOME_SEO } from "@/components/Seo";

const BlogIndex = lazy(() => import("@/pages/BlogIndex"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const StartFree = lazy(() => import("@/pages/StartFree"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const GorgiasComparison = lazy(() => import("@/pages/GorgiasComparison"));

const scrollToHash = (hash, attempts = 12) => {
  const el = hash && document.querySelector(hash);
  if (el) {
    window.__lenis?.resize();
    window.__lenis?.scrollTo(el, { offset: -110, duration: 1.4 });
  } else if (attempts > 0) setTimeout(() => scrollToHash(hash, attempts - 1), 250);
};

const useLenis = () => {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.14, smoothWheel: true, prevent: (node) => Boolean(node.closest?.("#qd-widget-container")) });
    window.__lenis = lenis;
    const onClick = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      e.preventDefault();
      scrollToHash(a.getAttribute("href"), 0);
    };
    document.addEventListener("click", onClick);
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("click", onClick);
      lenis.destroy();
    };
  }, []);
};

const Landing = () => (
  <>
    <Seo {...HOME_SEO} />
    <Hero />
    <PricingStrip />
    <Growing />
    <B2B />
    <D2C />
    <Audit />
    <Industries />
    <Integrations />
    <Testimonials />
    <Agents />
    <FinalCTA />
  </>
);

const Page = ({ children }) => <div className="qd-site qd-page">{children}</div>;

const RouteEffects = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) setTimeout(() => scrollToHash(hash), 350);
    else window.__lenis?.scrollTo(0, { immediate: true });
  }, [pathname, hash]);
  return null;
};

function App() {
  useLenis();
  return (
    <BrowserRouter>
      <BookingProvider>
        <RouteEffects />
        <div className="relative min-h-screen bg-mesh">
          <div className="relative z-10">
            <Nav />
            <main>
              <Suspense fallback={<div className="min-h-screen" />}>
                <Routes>
                  <Route path="/" element={<Landing />} />
                  <Route path="/pricing" element={<Page><Pricing /></Page>} />
                  <Route path="/start-free" element={<Page><StartFree /></Page>} />
                  <Route path="/blog" element={<BlogIndex />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/terms-of-service" element={<Page><LegalPage slug="terms-of-service" /></Page>} />
                  <Route path="/privacy-policy" element={<Page><LegalPage slug="privacy-policy" /></Page>} />
                  <Route path="/quantumdesk-vs-gorgias" element={<Page><GorgiasComparison /></Page>} />
                  <Route path="*" element={<Landing />} />
                </Routes>
              </Suspense>
            </main>
            <Footer />
          </div>
        </div>
        <Toaster position="bottom-left" richColors />
      </BookingProvider>
    </BrowserRouter>
  );
}

export default App;
